var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * React-query hooks for the semantic catalog (local ontology, ADR-0007 /
 * CZNA-419).
 *
 * The artifact is a classpath resource and changes only on deploy, so it is
 * cached aggressively — mirrors `useMarcCatalog.ts`. The label trace and its
 * tally are the exception (`LIVE_TEMPLATES`): they read live templates.
 * Closure-captures `ky` (per MEMORY: `useOkapiKy` returns an unstable instance and must NOT go into
 * dependency arrays).
 */
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchSemanticCatalog, fetchSemanticCatalogTrace, fetchSemanticCatalogTraceSummary, fetchComponentBindings, } from '../api/semanticCatalogService';
var CACHE = {
    staleTime: 60 * 60 * 1000,
    cacheTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
};
/**
 * The trace and its tally are NOT the classpath artifact: they read the live
 * form-config templates, which seven settings mutations rewrite without a deploy.
 * Under {@link CACHE} a template edit stayed invisible here for an hour, and a
 * tally fetched before the edit could list a field whose freshly fetched rows
 * no longer disagree. Refetched on every mount of the browser instead, and on
 * returning to the tab, where a template edited in another tab would otherwise
 * leave the tally stale against the next field opened — both endpoints are cheap
 * (each template is read once per request).
 */
export var LIVE_TEMPLATES = {
    staleTime: 0,
    refetchOnWindowFocus: true,
};
/**
 * `enabled: false` for a caller whose user may not read the catalog (CZNA-1339: the
 * record form's subfield help, where a cataloguer would only collect a 403).
 */
export var useSemanticCatalog = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.enabled, enabled = _c === void 0 ? true : _c;
    var ky = useOkapiKy();
    return useQuery(['semantic-catalog', 'full'], function () { return fetchSemanticCatalog(ky); }, __assign(__assign({}, CACHE), { enabled: enabled }));
};
/** Enabled only once a tag is selected — the trace is a per-tag drill-down. */
export var useSemanticCatalogTrace = function (tag) {
    var ky = useOkapiKy();
    return useQuery(['semantic-catalog', 'trace', tag], function () { return fetchSemanticCatalogTrace(ky, tag); }, __assign(__assign({}, LIVE_TEMPLATES), { enabled: !!tag }));
};
/**
 * The catalogue-wide trace tally (CZNA-547). Enabled only where it is shown — the
 * MARC fields list — because it traces every field against every template.
 * Same freshness as the per-field trace ({@link LIVE_TEMPLATES}), so the tally
 * and the rows it links to describe the same templates.
 */
export var useSemanticCatalogTraceSummary = function (enabled) {
    var ky = useOkapiKy();
    return useQuery(['semantic-catalog', 'traceSummary'], function () { return fetchSemanticCatalogTraceSummary(ky); }, __assign(__assign({}, LIVE_TEMPLATES), { enabled: enabled }));
};
/**
 * React-query hook for the ontology's componentType bindings (CZNA-433; the
 * enum-backed restriction was dropped by CZNA-438) —
 * feeds the derivation-rule admin's cascading componentType picker
 * (`DerivationRuleDetail.tsx`), replacing the outgoing Semantic Component
 * Registry's `useSemanticRegistry`. Same aggressive caching (classpath
 * resource, changes only on deploy).
 */
export var useComponentBindings = function () {
    var ky = useOkapiKy();
    return useQuery(['semantic-catalog', 'componentBindings'], function () { return fetchComponentBindings(ky); }, CACHE);
};
export default useSemanticCatalog;
